import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import styles from './expandall.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { useLocale } from '@app/core/hooks/useLocale';
import { Toggle, ToggleSize } from '../toggle/Toggle';
import {
  setExpandAllIrrHurdleCalculationsState,
  setExpandAllSponsorIrrState,
  setIrrHurdleCalculationsTableExpandStates,
  setSponsorIrrTableExpandMultiples,
  setSponsorIrrTableExpandYears,
} from '@app/core/store/ui-values-slice';
import { useParams } from 'react-router-dom';
import { Pwerm2CalculationWaterfallCaseYearDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { useFocusMode } from '@app/shared/hooks/focus-mode';

export enum SponsorIRRAndHurdleTabs {
  SponsorIRR = 'SponsorIRR',
  IRRHurdleCalculations = 'IRRHurdleCalculations',
}

interface ExpandAllSponsorIRRAndHurdleProps {
  tab: SponsorIRRAndHurdleTabs;
}

const ExpandAllSponsorIRRAndHurdle: React.FC<ExpandAllSponsorIRRAndHurdleProps> = ({ tab }) => {
  const dispatch = useAppDispatch();
  const { l } = useLocale();
  const { caseId } = useParams<{ caseId: string }>();
  const storedHurdleCalcSelectedInstrument = useAppSelector(
    (state) =>
      state.uiValues.userSelections.sponsorIrrAndHurdle.irrHurdleCalculations.selectedInstrument
  );
  const expandAll = useAppSelector((state) =>
    tab === SponsorIRRAndHurdleTabs.SponsorIRR
      ? state.uiValues.expandAllSponsorIrr
      : state.uiValues.expandAllIrrHurdleCalculations
  );
  const sponsorIrrExpandYears = useAppSelector((state) => state.uiValues.sponsorIrrExpandYears);
  const sponsorIrrExpandMultiples = useAppSelector(
    (state) => state.uiValues.sponsorIrrExpandMultiples
  );
  const irrHurdleCalculationExpandState = useAppSelector(
    (state) => state.uiValues.irrHurdleCalculationsExpandStates
  );
  const waterfallResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall
  );
  const { isActive: focusModeActive } = useFocusMode();

  const { yearKeys, multipleKeys } = useMemo(() => {
    const yearsObj = caseId ? waterfallResults?.cases?.[caseId]?.years : undefined;
    const years: string[] = [];
    const multiples: string[] = [];
    if (yearsObj) {
      Object.entries(yearsObj).forEach(([key, yearObj]) => {
        const year = yearObj as Pwerm2CalculationWaterfallCaseYearDto;
        years.push(key);
        if (year.multiples) {
          Object.keys(year.multiples).forEach((multipleId) => {
            multiples.push(`${year.exitDate}-${multipleId}`);
          });
        }
      });
    }
    return { yearKeys: years, multipleKeys: multiples };
  }, [caseId, waterfallResults?.cases]);

  const caseInstrumentConditionReferences = useMemo(() => {
    if (
      tab !== SponsorIRRAndHurdleTabs.IRRHurdleCalculations ||
      !caseId ||
      !waterfallResults?.cases?.[caseId]?.years
    ) {
      return [];
    }

    const instrumentIds = storedHurdleCalcSelectedInstrument
      ? [storedHurdleCalcSelectedInstrument]
      : Object.keys(waterfallResults.instruments);

    const years = Object.values(waterfallResults?.cases?.[caseId]?.years || {});

    const allReferences = instrumentIds.flatMap((instrumentId) =>
      years.flatMap((yearObj) => {
        const instrument = yearObj.instruments?.[instrumentId];

        if (!instrument || !instrument.tranches) {
          return [];
        }

        return Object.values(instrument.tranches).flatMap((tranche) =>
          (tranche.conditions || []).map(
            (condition) => condition.irrHurdleDetails?.conditionReference
          )
        );
      })
    );

    return Array.from(new Set(allReferences.filter(Boolean)));
  }, [tab, caseId, storedHurdleCalcSelectedInstrument, waterfallResults]);

  const allItemsExpanded = () => {
    switch (tab) {
      case SponsorIRRAndHurdleTabs.SponsorIRR:
        return (
          yearKeys.length > 0 &&
          multipleKeys.length > 0 &&
          yearKeys.every((date) => sponsorIrrExpandYears[date] === true) &&
          multipleKeys.every((date) => sponsorIrrExpandMultiples[date] === true)
        );
      case SponsorIRRAndHurdleTabs.IRRHurdleCalculations:
        return (
          caseInstrumentConditionReferences.length > 0 &&
          caseInstrumentConditionReferences.every(
            (conditionReference) => irrHurdleCalculationExpandState[conditionReference] === true
          )
        );
      default:
        return false;
    }
  };

  const forcedChecked = allItemsExpanded();

  useEffect(() => {
    tab === SponsorIRRAndHurdleTabs.SponsorIRR
      ? dispatch(setExpandAllSponsorIrrState(forcedChecked))
      : dispatch(setExpandAllIrrHurdleCalculationsState(forcedChecked));
  }, [dispatch, forcedChecked, tab]);

  const handleChange = () => {
    const newExpandAllState = !expandAll;
    if (tab === SponsorIRRAndHurdleTabs.SponsorIRR) {
      const newMultipleExpandStates = { ...sponsorIrrExpandMultiples };

      multipleKeys.forEach((key) => {
        newMultipleExpandStates[key] = newExpandAllState;
      });

      if (newExpandAllState) {
        const newYearExpandStates = { ...sponsorIrrExpandYears };
        yearKeys.forEach((key) => {
          newYearExpandStates[key] = true;
        });
        dispatch(setSponsorIrrTableExpandYears(newYearExpandStates));
      }

      dispatch(setExpandAllSponsorIrrState(newExpandAllState));
      dispatch(setSponsorIrrTableExpandMultiples(newMultipleExpandStates));
    } else {
      const newTableExpandStates = { ...irrHurdleCalculationExpandState };
      caseInstrumentConditionReferences.forEach((conditionReference) => {
        newTableExpandStates[conditionReference] = newExpandAllState;
      });
      dispatch(setExpandAllIrrHurdleCalculationsState(newExpandAllState));
      dispatch(setIrrHurdleCalculationsTableExpandStates(newTableExpandStates));
    }
  };

  const formMethods = useForm({
    ...formConfigBase,
  });

  return (
    <div style={focusModeActive ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
      <FormProvider {...formMethods}>
        <div className={styles['expand-all-container']}>
          <Toggle
            name="expandAll"
            label={l('_ExpandAllRows')}
            toggleSize={ToggleSize.Medium}
            className={styles['expand-all']}
            forceChecked={forcedChecked}
            onChange={handleChange}
            ignoreReadOnly
            disabled={focusModeActive}
            data-testid="expand-all-toggle"
          />
        </div>
      </FormProvider>
    </div>
  );
};

export default ExpandAllSponsorIRRAndHurdle;
